import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { ConfirmDialog } from '../components/ConfirmDialog';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-sm)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: 'var(--spacing-1)',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-accent)',
  border: 'none',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 500,
  cursor: 'pointer',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23999' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  paddingRight: '32px',
};

const statusBadge = (status: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  backgroundColor: status === 'fulfilled' ? 'rgba(40, 167, 69, 0.12)' : 'rgba(217, 178, 86, 0.15)',
  color: status === 'fulfilled' ? '#28a745' : '#b8941f',
});

const typeBadge = (type: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  backgroundColor: type === 'inventory' ? 'rgba(0, 123, 255, 0.1)' : 'rgba(108, 117, 125, 0.1)',
  color: type === 'inventory' ? '#007bff' : '#6c757d',
});

// ─── Autocomplete input ─────────────────────────────────────────────────────

function AutocompleteInput({
  label,
  placeholder,
  items,
  onSearch,
  onSelect,
  selectedName,
  onClear,
  loading,
  allowCreate,
}: {
  label: string;
  placeholder: string;
  items: any[];
  onSearch: (term: string) => void;
  onSelect: (item: any) => void;
  selectedName: string;
  onClear: () => void;
  loading?: boolean;
  allowCreate?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (search.trim().length >= 2) {
      const t = setTimeout(() => onSearch(search.trim()), 250);
      return () => clearTimeout(t);
    }
  }, [search, onSearch]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropRef.current &&
        !dropRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (selectedName) {
    return (
      <div>
        <label style={labelStyle}>{label}</label>
        <div
          style={{
            ...inputStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'rgba(217,178,86,0.06)',
            borderColor: 'rgba(217,178,86,0.25)',
          }}
        >
          <span style={{ fontWeight: 500 }}>{selectedName}</span>
          <button
            type="button"
            onClick={onClear}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              color: 'var(--color-text-muted)',
              padding: '0 2px',
            }}
          >
            &times;
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <label style={labelStyle}>{label}</label>
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (items.length > 0) setOpen(true);
        }}
        placeholder={placeholder}
        style={inputStyle}
        autoComplete="off"
      />
      {open && (items.length > 0 || loading || (allowCreate && search.trim().length >= 2)) && (
        <div
          ref={dropRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            border: '1px solid var(--color-border, #ddd)',
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            zIndex: 20,
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          {loading && (
            <div
              style={{ padding: '10px 12px', color: 'var(--color-text-muted)', fontSize: '13px' }}
            >
              Searching...
            </div>
          )}
          {items.map((item) => (
            <div
              key={item.id}
              onClick={() => {
                onSelect(item);
                setSearch('');
                setOpen(false);
              }}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid rgba(0,0,0,0.05)',
                fontSize: '13px',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(217,178,86,0.08)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
              }}
            >
              <div style={{ fontWeight: 600 }}>{item.name}</div>
              {item.subtitle && (
                <div style={{ fontSize: '11px', opacity: 0.6 }}>{item.subtitle}</div>
              )}
            </div>
          ))}
          {allowCreate &&
            search.trim().length >= 2 &&
            !items.some((i) => i.name.toLowerCase() === search.trim().toLowerCase()) && (
              <div
                onClick={() => {
                  onSelect({ id: null, name: search.trim(), isNew: true });
                  setSearch('');
                  setOpen(false);
                }}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'background 0.15s',
                  borderTop: items.length > 0 ? '1px solid rgba(0,0,0,0.1)' : 'none',
                  color: '#D9B256',
                  fontWeight: 600,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(217,178,86,0.08)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                }}
              >
                + Create &quot;{search.trim()}&quot;
              </div>
            )}
        </div>
      )}
    </div>
  );
}

// ─── Request Form ─────────────────────────────────────────────────────────

function RequestForm({
  locations,
  defaultLocationId,
  isAdmin,
  onClose,
}: {
  locations: any[];
  defaultLocationId?: number;
  isAdmin: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [itemType, setItemType] = useState<'inventory' | 'supply'>('inventory');
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [quantity, setQuantity] = useState('');
  const [locationId, setLocationId] = useState(String(defaultLocationId || locations[0]?.id || ''));
  const [notes, setNotes] = useState('');
  const [success, setSuccess] = useState(false);

  // Inventory search
  const [invResults, setInvResults] = useState<any[]>([]);
  const [invLoading, setInvLoading] = useState(false);
  const allInventory = useRef<any[]>([]);

  const loadInventory = useCallback(async () => {
    if (allInventory.current.length === 0) {
      try {
        allInventory.current = await api.getInventory();
      } catch {
        /* empty */
      }
    }
  }, []);

  const searchInventory = useCallback(
    async (term: string) => {
      setInvLoading(true);
      await loadInventory();
      const lower = term.toLowerCase();
      setInvResults(
        allInventory.current
          .filter(
            (i: any) =>
              i.name?.toLowerCase().includes(lower) || i.sku?.toLowerCase().includes(lower),
          )
          .slice(0, 10)
          .map((i: any) => ({
            id: i.id,
            name: i.name,
            subtitle: [
              i.category,
              i.sku ? `SKU ${i.sku}` : '',
              `$${Number(i.price || 0).toFixed(2)}`,
            ]
              .filter(Boolean)
              .join(' · '),
            raw: i,
          })),
      );
      setInvLoading(false);
    },
    [loadInventory],
  );

  // Supply search
  const [supResults, setSupResults] = useState<any[]>([]);
  const [supLoading, setSupLoading] = useState(false);
  const allSupplies = useRef<any[]>([]);

  const loadSupplies = useCallback(async () => {
    if (allSupplies.current.length === 0) {
      try {
        allSupplies.current = await api.getSupplies();
      } catch {
        /* empty */
      }
    }
  }, []);

  const searchSupplies = useCallback(
    async (term: string) => {
      setSupLoading(true);
      await loadSupplies();
      const lower = term.toLowerCase();
      setSupResults(
        allSupplies.current
          .filter(
            (s: any) =>
              s.name?.toLowerCase().includes(lower) || s.category?.toLowerCase().includes(lower),
          )
          .slice(0, 10)
          .map((s: any) => ({
            id: s.id,
            name: s.name,
            subtitle: [s.category, s.unit].filter(Boolean).join(' · '),
            raw: s,
          })),
      );
      setSupLoading(false);
    },
    [loadSupplies],
  );

  const createMutation = useMutation({
    mutationFn: api.createSupplyRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supply-requests'] });
      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem || !quantity || Number(quantity) < 1 || !locationId) return;
    createMutation.mutate({
      item_type: itemType,
      inventory_item_id: itemType === 'inventory' ? selectedItem.id : undefined,
      supply_id: itemType === 'supply' && selectedItem.id ? selectedItem.id : undefined,
      supply_name: itemType === 'supply' && !selectedItem.id ? selectedItem.name : undefined,
      item_name: selectedItem.name,
      quantity: Number(quantity),
      location_id: Number(locationId),
      notes: notes.trim() || undefined,
    });
  };

  const quantityNum = Number(quantity);
  const canSubmit = selectedItem && quantity && quantityNum > 0 && locationId;

  if (success) {
    return (
      <div
        style={{
          ...cardStyle,
          marginBottom: 'var(--spacing-4)',
          textAlign: 'center',
          padding: 'var(--spacing-6)',
        }}
      >
        <div style={{ fontSize: '32px', marginBottom: 'var(--spacing-2)' }}>✓</div>
        <p style={{ fontWeight: 600, fontSize: 'var(--font-size-lg)' }}>Request Submitted</p>
        <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
          {selectedItem.name} × {quantity} has been requested.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
      <h3 style={{ margin: '0 0 var(--spacing-3)' }}>New Request</h3>

      <div style={{ display: 'grid', gap: 'var(--spacing-3)', gridTemplateColumns: '1fr 1fr' }}>
        <div>
          <label style={labelStyle}>Item Type</label>
          <select
            value={itemType}
            onChange={(e) => {
              setItemType(e.target.value as any);
              setSelectedItem(null);
            }}
            style={selectStyle}
          >
            <option value="inventory">Inventory</option>
            <option value="supply">Supplies</option>
          </select>
        </div>
        {isAdmin && (
          <div>
            <label style={labelStyle}>Location</label>
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              style={selectStyle}
            >
              {locations.map((l: any) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{ marginTop: 'var(--spacing-3)' }}>
        {itemType === 'inventory' ? (
          <AutocompleteInput
            label="Inventory Item"
            placeholder="Search products..."
            items={invResults}
            onSearch={searchInventory}
            onSelect={(item) => setSelectedItem(item)}
            selectedName={selectedItem?.name || ''}
            onClear={() => setSelectedItem(null)}
            loading={invLoading}
          />
        ) : (
          <AutocompleteInput
            label="Supply Item"
            placeholder="Search or type new supply name..."
            items={supResults}
            onSearch={searchSupplies}
            onSelect={(item) => setSelectedItem(item)}
            selectedName={selectedItem?.name || ''}
            onClear={() => setSelectedItem(null)}
            loading={supLoading}
            allowCreate
          />
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gap: 'var(--spacing-3)',
          gridTemplateColumns: '120px 1fr',
          marginTop: 'var(--spacing-3)',
        }}
      >
        <div>
          <label style={labelStyle}>Quantity</label>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="0"
            required
            style={inputStyle}
          />
          {quantity && quantityNum < 1 && (
            <p style={{ color: 'var(--color-error)', fontSize: '11px', marginTop: '4px' }}>
              Must be ≥ 1
            </p>
          )}
        </div>
        <div>
          <label style={labelStyle}>Notes (optional)</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional details..."
            style={inputStyle}
          />
        </div>
      </div>

      {createMutation.isError && (
        <p
          style={{
            color: 'var(--color-error)',
            fontSize: 'var(--font-size-sm)',
            marginTop: 'var(--spacing-2)',
            padding: 'var(--spacing-2)',
            backgroundColor: 'rgba(220,53,69,0.08)',
            borderRadius: 'var(--radius-default)',
          }}
        >
          {(createMutation.error as Error)?.message || 'Failed to create request'}
        </p>
      )}

      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-4)' }}>
        <button
          type="submit"
          disabled={!canSubmit || createMutation.isPending}
          style={{
            ...primaryButtonStyle,
            opacity: !canSubmit || createMutation.isPending ? 0.5 : 1,
          }}
        >
          {createMutation.isPending ? 'Submitting...' : 'Submit Request'}
        </button>
        <button type="button" onClick={onClose} style={secondaryButtonStyle}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function SupplyRequests() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'ADMIN';
  const [showForm, setShowForm] = useState(false);
  const [pendingFulfill, setPendingFulfill] = useState<any | null>(null);

  // Filters
  const [filterLocation, setFilterLocation] = useState(
    !isAdmin && user?.locationId ? String(user.locationId) : '',
  );
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterType, setFilterType] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: api.getLocations,
  });

  const filterParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (filterLocation) p.locationId = filterLocation;
    if (filterStatus) p.status = filterStatus;
    if (filterType) p.itemType = filterType;
    if (filterDateFrom) p.dateFrom = filterDateFrom;
    if (filterDateTo) p.dateTo = filterDateTo;
    return p;
  }, [filterLocation, filterStatus, filterType, filterDateFrom, filterDateTo]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['supply-requests', filterParams],
    queryFn: () => api.getSupplyRequests(filterParams),
  });

  const requests = data?.requests || [];
  const total = data?.total || 0;

  const fulfillMutation = useMutation({
    mutationFn: api.fulfillSupplyRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supply-requests'] });
      setPendingFulfill(null);
    },
  });

  const formatDate = (d: string) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Australia/Brisbane',
    });
  };

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <div>
          <h1>Supply Requests</h1>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-2)' }}>
            Request inventory products or backend supplies.{' '}
            {isAdmin ? 'Manage and fulfil requests.' : ''}
          </p>
        </div>
        <button onClick={() => setShowForm(true)} style={primaryButtonStyle}>
          + New Request
        </button>
      </div>

      {/* Request Form */}
      {showForm && (
        <RequestForm
          locations={locations}
          defaultLocationId={user?.locationId}
          isAdmin={isAdmin}
          onClose={() => setShowForm(false)}
        />
      )}

      {/* Filters */}
      <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
        <div
          style={{
            display: 'grid',
            gap: 'var(--spacing-3)',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          }}
        >
          {isAdmin && (
            <div>
              <label style={labelStyle}>Location</label>
              <select
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                style={selectStyle}
              >
                <option value="">All Locations</option>
                {locations.map((l: any) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={labelStyle}>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={selectStyle}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="fulfilled">Fulfilled</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={selectStyle}
            >
              <option value="">All</option>
              <option value="inventory">Inventory</option>
              <option value="supply">Supply</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>From</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <section style={cardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          <h2 style={{ margin: 0 }}>Requests</h2>
          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            {total} result{total !== 1 ? 's' : ''}
          </span>
        </div>

        {isLoading ? (
          <p style={{ color: 'var(--color-text-muted)' }}>Loading requests...</p>
        ) : isError ? (
          <p style={{ color: 'var(--color-error)' }}>Failed to load requests. Please try again.</p>
        ) : requests.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: 'var(--spacing-6)',
              color: 'var(--color-text-muted)',
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: 'var(--spacing-2)' }}>📋</div>
            <p style={{ fontWeight: 500 }}>No requests found</p>
            <p style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--spacing-1)' }}>
              Adjust your filters or create a new request.
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            >
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                  {[
                    'ID',
                    'Item',
                    'Type',
                    'Qty',
                    'Location',
                    'Requested At',
                    'Status',
                    isAdmin ? 'Action' : '',
                  ]
                    .filter(Boolean)
                    .map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-2) var(--spacing-3)',
                          fontWeight: 600,
                          color: 'var(--color-text-muted)',
                          fontSize: 'var(--font-size-xs)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {requests.map((r: any) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      #{r.id}
                    </td>
                    <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                      <div style={{ fontWeight: 500 }}>{r.item_name}</div>
                      {r.notes && (
                        <div
                          style={{
                            fontSize: '11px',
                            color: 'var(--color-text-muted)',
                            marginTop: '2px',
                          }}
                        >
                          {r.notes}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                      <span style={typeBadge(r.item_type)}>{r.item_type}</span>
                    </td>
                    <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', fontWeight: 600 }}>
                      {r.quantity}
                    </td>
                    <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                      {r.location_name || `#${r.location_id}`}
                    </td>
                    <td
                      style={{ padding: 'var(--spacing-2) var(--spacing-3)', whiteSpace: 'nowrap' }}
                    >
                      {formatDate(r.requested_at)}
                    </td>
                    <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                      <span style={statusBadge(r.status)}>{r.status}</span>
                      {r.status === 'fulfilled' && r.fulfilled_by_name && (
                        <div
                          style={{
                            fontSize: '11px',
                            color: 'var(--color-text-muted)',
                            marginTop: '2px',
                          }}
                        >
                          by {r.fulfilled_by_name} · {formatDate(r.fulfilled_at)}
                        </div>
                      )}
                    </td>
                    {isAdmin && (
                      <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                        {r.status === 'pending' && (
                          <button
                            onClick={() => setPendingFulfill(r)}
                            style={{
                              padding: '4px 12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor: 'rgba(40, 167, 69, 0.1)',
                              border: '1px solid rgba(40, 167, 69, 0.25)',
                              borderRadius: 'var(--radius-default)',
                              color: '#28a745',
                              cursor: 'pointer',
                            }}
                          >
                            Fulfil
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Fulfil confirmation dialog */}
      <ConfirmDialog
        isOpen={!!pendingFulfill}
        title="Fulfil Request"
        message={
          pendingFulfill
            ? `Mark "${pendingFulfill.item_name}" × ${pendingFulfill.quantity} as fulfilled?`
            : ''
        }
        confirmLabel={fulfillMutation.isPending ? 'Fulfilling...' : 'Fulfil'}
        tone="warning"
        onConfirm={() => {
          if (pendingFulfill) fulfillMutation.mutate(pendingFulfill.id);
        }}
        onCancel={() => setPendingFulfill(null)}
      />
    </div>
  );
}
